import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query, queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import FollowingFeedWithSortByStorageSnippetsQuery, {
	type FollowingFeedWithSortByStorageSnippetsQuery$data,
	type FollowingFeedWithSortByStorageSnippetsQuery$variables,
} from './__generated__/FollowingFeedWithSortByStorageSnippetsQuery.graphql.ts';
import useFeedTypeSelectorRefreshQuery, {
	type useFeedTypeSelectorRefreshQuery$data,
	type useFeedTypeSelectorRefreshQuery$variables,
} from './__generated__/useFeedTypeSelectorRefreshQuery.graphql.ts';
import usePopularFeedWithTimeGranularityStorageQuery, {
	type usePopularFeedWithTimeGranularityStorageQuery$data,
	type usePopularFeedWithTimeGranularityStorageQuery$variables,
} from './__generated__/usePopularFeedWithTimeGranularityStorageQuery.graphql.ts';
import PopularFeedWithTimeGranularityStorageSnippetsV2Query, {
	type PopularFeedWithTimeGranularityStorageSnippetsV2Query$data,
	type PopularFeedWithTimeGranularityStorageSnippetsV2Query$variables,
} from './__generated__/PopularFeedWithTimeGranularityStorageSnippetsV2Query.graphql.ts';

export const preloadFeed = ({
	isLoggedIn,
}: {
	isLoggedIn: any;
}): Promise<void | any[] | Error[]> => {
	// If the user is not logged in we show the following feed by default
	// so only preload that feed. If the user is logged in preload both
	// feeds and the feed preference

	const { isRovoLLMEnabled } = getPreloaderFnContext();

	const followingFeedQuery = queryAgg<
		FollowingFeedWithSortByStorageSnippetsQuery$data,
		FollowingFeedWithSortByStorageSnippetsQuery$variables
	>({
		query: FollowingFeedWithSortByStorageSnippetsQuery,
		variables: {
			first: expVal('confluence_home_feed_initial_page_size', 'following_ssr', 5),
		},
	});

	// Select the popular-feed query to preload. Decision order MUST mirror
	// `usePopularFeedWithTimeGranularityStorage`'s client-side selection so the
	// SSR-cached response matches the query the client fires on hydration.
	// Otherwise SSR users would suffer a cache miss + re-fetch, negating the
	// latency win for the very users we care most about (first paint).
	//
	//   - V2 (parallel)            when isRovoLLMEnabled
	//   - vanilla V1               otherwise
	const popularFirstCount = fg('confluence_ai_feeds_home_banner')
		? 10
		: expVal('confluence_home_feed_initial_page_size', 'popular_ssr', 5);

	let popularFeedDataQuery;
	if (isRovoLLMEnabled) {
		// V2 (parallel hydration) — node-level aiProperty, AGG calls cc-smarts
		// in parallel with the Confluence content hop. We only enter this
		// branch when `isRovoLLMEnabled` is true, so the V2 query variable can
		// be passed `true` unconditionally.
		popularFeedDataQuery = queryAgg<
			PopularFeedWithTimeGranularityStorageSnippetsV2Query$data,
			PopularFeedWithTimeGranularityStorageSnippetsV2Query$variables
		>({
			query: PopularFeedWithTimeGranularityStorageSnippetsV2Query,
			variables: {
				first: popularFirstCount,
				isRovoLLMEnabled: true,
			},
		});
	} else {
		// Vanilla V1 (no AI snippets) — the safe fallback for non-Rovo users,
		// who cannot be served AI snippets at all.
		popularFeedDataQuery = query<
			usePopularFeedWithTimeGranularityStorageQuery$data,
			usePopularFeedWithTimeGranularityStorageQuery$variables
		>({
			query: usePopularFeedWithTimeGranularityStorageQuery,
			variables: {
				first: popularFirstCount,
			},
		});
	}

	const popularFeedQuery = [
		popularFeedDataQuery,
		query<useFeedTypeSelectorRefreshQuery$data, useFeedTypeSelectorRefreshQuery$variables>({
			query: useFeedTypeSelectorRefreshQuery,
		}),
	];
	const tasks: Array<Promise<any>> = [followingFeedQuery];
	if (isLoggedIn) {
		tasks.push(...popularFeedQuery);
	}
	return waitForAllReturnWhenError(tasks);
};
